import React from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from 'recharts'
import { Ticket } from '../../services/tickets'

interface ActiveIssuesChartProps {
  tickets: Ticket[]
}

export const ActiveIssuesChart: React.FC<ActiveIssuesChartProps> = ({ tickets }) => {
  // Group tickets by priority and calculate counts
  const priorityCounts = tickets.reduce((acc, ticket) => {
    const priority = ticket.priority === 'urgent' ? 'high' : ticket.priority
    acc[priority] = (acc[priority] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  // Prepare data for the chart
  const chartData = [
    {
      name: 'High',
      count: priorityCounts.high || 0,
      color: '#ef4444'
    },
    {
      name: 'Medium', 
      count: priorityCounts.medium || 0,
      color: '#f97316'
    },
    {
      name: 'Low',
      count: priorityCounts.low || 0,
      color: '#6b7280'
    }
  ]

  // Calculate max value for Y-axis scaling
  const maxCount = Math.max(...chartData.map(item => item.count))
  const yAxisMax = maxCount === 0 ? 5 : Math.ceil(maxCount * 1.2)

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Issues</h3>
        
        {/* Legend */}
        <div className="flex items-center justify-center space-x-6 mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-sm text-gray-600">High Priority</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span className="text-sm text-gray-600">Medium Priority</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
            <span className="text-sm text-gray-600">Low Priority</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={260} minHeight={200}>
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis 
            dataKey="name" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6b7280' }}
          />
          <YAxis 
            domain={[0, yAxisMax]}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickCount={6}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

    </div>
  )
}
